/**
 * ChunkLoadError auto-recovery.
 *
 * When a new deployment ships, old cached index.html files reference JS/CSS
 * chunk filenames (content-hashed) that no longer exist on the server. When
 * a user navigates to a lazy-loaded route, React.lazy()'s dynamic import()
 * fails with a ChunkLoadError / "Failed to fetch dynamically imported module".
 * This propagates to the nearest ErrorBoundary, showing "Something went wrong".
 *
 * This module detects chunk-load failures and automatically reloads the page
 * with a cache-busting query parameter so the browser fetches a fresh
 * index.html that references the correct (current) chunk hashes.
 *
 * Infinite-loop guard: if the page was already reloaded by this handler
 * (detected via the _rd query param), subsequent chunk errors fall through
 * to the ErrorBoundary instead of triggering another reload. This prevents
 * a reload loop if the chunk is genuinely broken (not just stale).
 *
 * Wired in two places:
 *   1. src/index.js — global listeners (unhandledrejection + error events)
 *      catch failures that surface outside React's render phase.
 *   2. src/components/common/ErrorBoundary.jsx — componentDidCatch checks
 *      the error so failures caught by the boundary trigger a reload before
 *      the error UI is shown.
 */

let _isReloading = false;

/**
 * Returns true if the page was already reloaded by this handler.
 * Detected via the _rd query parameter — handleChunkLoadError adds it
 * before reloading. If it's present on the current URL, we already tried
 * reloading and it didn't fix the issue, so we must NOT reload again.
 */
const _wasReloadedByHandler = () => {
  try {
    return new URL(window.location.href).searchParams.has('_rd');
  } catch (_e) {
    return false;
  }
};

/**
 * Returns true if the error message looks like a chunk/script load failure.
 * Covers webpack 4 (CRA), webpack 5, and native dynamic-import error strings.
 */
export const isChunkLoadError = (error) => {
  if (!error) return false;
  const message = typeof error === 'string' ? error : (error.message || String(error));
  if (!message) return false;
  const lower = message.toLowerCase();
  return (
    lower.includes('loading chunk') ||
    lower.includes('loading css chunk') ||
    lower.includes('loading script chunk') ||
    lower.includes('chunkloaderror') ||
    lower.includes('failed to fetch dynamically imported module') ||
    lower.includes('importing a module script failed') ||
    lower.includes('error loading module from server') ||
    // Safari: "Importing a module script failed."
    lower.includes('importing a module script')
  );
};

/**
 * Reloads the page to fetch a fresh index.html with correct chunk hashes.
 * Idempotent within a single page load — only reloads once even if multiple
 * chunk errors fire. Also guarded against infinite reload loops: if the
 * current URL already has the _rd param (from a previous reload), the
 * function returns false so the ErrorBoundary can show the error UI instead.
 *
 * Returns true if a reload was triggered (or already in progress).
 * Returns false if the error is not a chunk error, or if we already
 * reloaded and shouldn't try again (loop guard).
 */
export const handleChunkLoadError = (error) => {
  if (_isReloading) return true; // already handling within this page load
  if (!isChunkLoadError(error)) return false;

  // Loop guard: if _rd is already in the URL, we already tried reloading
  // and the chunk is still broken. Don't reload again — let the
  // ErrorBoundary show the error UI so the user can report the issue.
  if (_wasReloadedByHandler()) return false;

  _isReloading = true;

  // Add a cache-busting param so CDN/browser caches serve a fresh index.html.
  // Using location.replace() so the broken page doesn't stay in browser history.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_rd', String(Date.now()));
    window.location.replace(url.toString());
  } catch (_e) {
    // Fallback: plain reload if URL manipulation fails
    window.location.reload();
  }

  return true;
};

/**
 * Installs global listeners for chunk-load failures that surface outside
 * React's render phase (e.g., async import() rejections not caught by an
 * error boundary). Call once at app startup.
 */
export const installChunkErrorHandler = () => {
  if (typeof window === 'undefined') return;

  // Dynamic import() failures can surface as unhandled promise rejections.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (handleChunkLoadError(reason)) {
      // Prevent the rejection from also triggering the ErrorBoundary
      // (which would flash the error UI before the reload completes).
      event.preventDefault();
    }
  });

  // <script> tag load failures surface as error events in the capture phase.
  // Resource load errors (failed <script>/<img>/<link>) fire as Event — not
  // ErrorEvent — so event.message is undefined and event.error is null. We
  // check event.target for <script> tags pointing to our own /static/js/
  // chunks. This catches lazy-chunk failures at the script-tag level (before
  // webpack's promise rejection propagates to the ErrorBoundary), providing
  // faster recovery. Third-party script failures (analytics, etc.) are ignored.
  window.addEventListener('error', (event) => {
    const target = event.target;
    if (target && target.tagName === 'SCRIPT' && target.src && target.src.includes('/static/js/')) {
      handleChunkLoadError(new Error('Loading script chunk failed: ' + target.src));
      return;
    }
    handleChunkLoadError(event.message || event.error);
  }, true); // capture phase — catches resource load errors
};
